import { useTranslation } from '../../i18n';
import React from 'react';
import { Alert, Button } from '@parsina/framework';
import { X } from 'lucide-react';

import {
  isContactCheckout, remainingLabel, shouldWarn, statusMeta,
} from '../../screens/Subscription/constants';

/**
 * App-wide subscription banner — canonical alert zone ② (design §2.2).
 *
 * Renders nothing unless something genuinely needs attention: an entitled,
 * comfortably-in-date clinic must never see a billing banner. A permanent nag
 * trains people to ignore the alert zone, which then fails to work when a real
 * safety banner (allergy, forgotten visit) appears there.
 *
 * Shows for EXPIRED / PAST_DUE / SUSPENDED, and as a soft nudge in the last
 * three days of a trial — early enough to act on, late enough not to nag.
 *
 * Hidden while the subscription service is unreachable (`unavailable`): the BFF
 * fails open in that case, so the clinic is not actually restricted and telling
 * it otherwise would be a lie.
 *
 * ── Dismissal (`dismissible`, phones only) ──────────────────────────────────
 *
 * On a workstation the banner costs a strip of a wide screen. On a phone it
 * costs a slice of the only screen there is, above every list the operator is
 * trying to work — and «۱ روز باقی مانده» is the same sentence on the tenth
 * reading as on the first. So the phone gets an «×».
 *
 * **What is dismissed is the STATUS, not the banner.** The choice is stored per
 * clinic as the status it was made about, so:
 *
 *   · dismissing the trial nudge silences it for that clinic, for good — it
 *     does not return tomorrow at «امروز آخرین روز است»;
 *   · if the clinic later LAPSES — EXPIRED, PAST_DUE, SUSPENDED — that is a
 *     different status and a different sentence, and it shows.
 *
 * That second half is the load-bearing one. A dismissal that silenced every
 * future billing message would mean one tap during a calm trial permanently
 * hides the notice that the clinic is now blocked from registering patients —
 * and the operator would never learn why the panel stopped working. «Do not
 * come back» is a promise about the message that was dismissed, not about every
 * message this component will ever carry.
 *
 * Kept in `localStorage`, so it is per device and per browser. That is the right
 * scope for «I have read this»: it is a UI preference, not a clinic fact, and
 * nothing downstream reads it. Every access is wrapped — a private window, or a
 * browser with site data blocked, throws on read as well as on write — and the
 * banner renders normally when storage is unavailable rather than disappearing.
 */

/** One key per clinic; the value is the status that was dismissed. */
const storageKey = (clinicRef) => `parsina.subscriptionBanner.dismissed.${clinicRef || 'unknown'}`;

function readDismissed(clinicRef) {
  try {
    return window.localStorage.getItem(storageKey(clinicRef)) || '';
  } catch {
    // Private window, or site data blocked. Never a reason to hide the banner.
    return '';
  }
}

function writeDismissed(clinicRef, status) {
  try {
    window.localStorage.setItem(storageKey(clinicRef), status);
  } catch {
    // The dismissal does not survive the session. The tap still works — the
    // banner is already gone from this render — which is the part the operator
    // asked for.
  }
}

export default function SubscriptionBanner({
  capabilities, onRenew, compact = false, dismissible = false, clinicRef = '',
}) {
  const { t } = useTranslation();

  const status = capabilities?.status || '';

  // Read at mount and whenever the CLINIC or the STATUS changes — the two things
  // that can make a stored dismissal stop applying. Lazy state rather than an
  // effect: an effect would render the banner for one frame and then pull it
  // away, which on a phone is a flash directly above what the operator is reading.
  const [dismissedStatus, setDismissedStatus] = React.useState(() => readDismissed(clinicRef));
  React.useEffect(() => { setDismissedStatus(readDismissed(clinicRef)); }, [clinicRef, status]);

  if (!shouldWarn(capabilities)) return null;
  // A READ-ONLY panel is not a nudge: every write on every screen will be
  // refused, so the banner saying why must not be dismissible, even on a phone.
  const canDismiss = dismissible && !capabilities.readOnly;
  if (canDismiss && dismissedStatus && dismissedStatus === status) return null;

  const meta = statusMeta(capabilities.status, t, capabilities.billing?.checkout);
  // No way to pay (checkout CONTACT): only a suspension reaches here, and it
  // offers no renew button — there is nothing to renew with.
  const contact = isContactCheckout(capabilities);
  const remaining = remainingLabel(capabilities.daysRemaining, t);

  // A trial nudge is informational; a lapse is not.
  const variant = capabilities.inTrial
    ? 'info'
    : meta.tone === 'warning' ? 'warning' : 'error';

  const message = capabilities.inTrial
    ? t('subscription.banner.trial', { remaining })
    : capabilities.paywallMessageFa || meta.description;

  const dismiss = () => {
    writeDismissed(clinicRef, status);
    setDismissedStatus(status);
  };

  const cta = onRenew && !contact ? (
    <Button size="sm" variant="default" onClick={onRenew} data-testid="subscription-banner-cta">
      {/* A lapsed trial never had a subscription to renew. */}
      {capabilities.inTrial || capabilities.trialEnded
        ? t('subscription.actions.activate')
        : t('subscription.actions.renew')}
    </Button>
  ) : null;

  // The kit's `Alert` at the pinned tag has no dismiss of its own, so the «×»
  // rides in the action slot beside the CTA rather than waiting on the kit.
  const action = (cta || canDismiss) ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {cta}
      {canDismiss ? (
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('common.actions.dismiss')}
          title={t('common.actions.dismiss')}
          data-testid="subscription-banner-dismiss"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            // 44px: the hit-target floor the rest of the mobile design keeps to,
            // beside a CTA it must not be mistaken for.
            width: '2.75rem', height: '2.75rem',
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'inherit', opacity: 0.7, flexShrink: 0,
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      ) : null}
    </span>
  ) : null;

  return (
    <div className={compact ? 'px-4 pt-3' : 'px-6 pt-4'} data-testid="subscription-banner">
      <Alert variant={variant} action={action}>
        {message}
      </Alert>
    </div>
  );
}
